import { FC, useEffect, useState } from 'react';
import RoundedButton from '../Buttons/RoundedButton';
import { useDispatch } from 'react-redux';
import { updateCreditPlanDebugMode } from '@/redux/slices/creditPlanSlice';
import Shimmer from '../Shimmer';
import type { CreditPlanResponseModel } from '@/@types/client-api';
import classnames from 'classnames';

type DebugButtonsProps = {
  allowed: boolean;
  isActive: boolean;
  planId?: string;
  isDeleting?: boolean;
  onDeletePress: () => void;
  planItem?: CreditPlanResponseModel;
};

const DebugButtons: FC<DebugButtonsProps> = ({
  allowed,
  isActive,
  planId,
  isDeleting,
  onDeletePress,
  planItem
}) => {
  const dispatch = useDispatch();
  const hasDebugActions = !!planId;
  const [showDebugActions, setShowDebugActions] = useState<boolean>(!!isActive && hasDebugActions);
  const [isAnimatingDebugActions, setIsAnimatingDebugActions] = useState<boolean>(false);

  useEffect(() => {
    if (isActive && hasDebugActions) {
      setShowDebugActions(true);
      setIsAnimatingDebugActions(true);

      const timeoutId = window.setTimeout(() => {
        setIsAnimatingDebugActions(false);
      }, 300);

      return () => {
        window.clearTimeout(timeoutId);
      };
    }

    setIsAnimatingDebugActions(true);

    const timeoutId = window.setTimeout(() => {
      setShowDebugActions(false);
      setIsAnimatingDebugActions(false);
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [hasDebugActions, isActive]);

  if (!allowed) return;

  if (isDeleting)
    return (
      <>
        <div className="mr-auto flex flex-row items-center justify-start">
          <Shimmer className="mr-2 size-10 rounded-full" />
          <Shimmer className="mr-2 size-10 rounded-full" />
          <Shimmer className="size-10 rounded-full" />
        </div>
      </>
    );

  const handleDownloadJson = () => {
    if (!planItem) return;
    const json = JSON.stringify(planItem, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const filename = `creditplan_${planItem?.licenseIdentifier || planId || 'unknown'}.json`;
    // Native browser download
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 0);
  };

  return (
    <div className="mr-auto flex flex-row items-center justify-start">
      <RoundedButton
        icon="info"
        buttonSize="large"
        title="Toggle debug mode"
        color={!isActive ? 'outline-black' : 'gray-darker'}
        className="rounded hover:shadow-lg"
        onClick={() => {
          dispatch(updateCreditPlanDebugMode(!isActive));
        }}
      />
      {hasDebugActions && showDebugActions && (
        <div className="relative ml-3 overflow-hidden">
          {isAnimatingDebugActions && (
            <div
              className="pointer-events-none absolute inset-y-0
              left-0 z-1 w-4 animate-fadeIn bg-gradient-to-r from-gray/25
              to-transparent"
            />
          )}
          <div
            className={classnames('flex flex-row items-center justify-start gap-3 duration-long', {
              'animate-in slide-in-from-left': !!isActive,
              'animate-out slide-out-to-left': !isActive
            })}
          >
            <RoundedButton
              color="red"
              icon="delete"
              buttonSize="large"
              title="Delete credit plan"
              onClick={() => onDeletePress()}
            />
            <RoundedButton
              color="black"
              icon="download"
              buttonSize="large"
              title="Download creditplan JSON"
              onClick={handleDownloadJson}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DebugButtons;
